import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Snackbar from '@material-ui/core/Snackbar';
import { useTranslation } from 'react-i18next';

// Internal imports
import { AuthContext } from '../../authentication/Auth';
// import { MessageResponseContext } from '../../data/MessageLocalizationProvider';
import { RESOURCES, usePermissions, withRBAC } from '../../authentication/rbac';
import { fetchKiosks, getTotalCount, fetchLastPage } from './kioskQueries';
import { useDGAMLicenseCheck } from '../../authentication/licensing';
import NoAccessMessage from "../../components/common/NoAccessMessage";

// Components
import BottomNavLinks from "../../components/BottomNavLinks";
import Toolbar from '../../components/common/Toolbar';
import Table from '../../components/common/Table';
import {
  ContentWrapper,
  CommonPageHeader,
  CommonPageTitle,
  SnackbarContent,
  CheckingAccessMessage,
} from '../../styled-components/shared';
import {
  EditRounded as EditIcon,
} from '@material-ui/icons';
import KioskModal from '../../components/modals/KioskModal';

const MainContent = styled.div`
  border-bottom: none;
  border: 1px solid #e0e3e9;
  margin-top: 10px;
  @media screen and (max-width: 750px) {
    margin-top: 60px;
  }
`;

const CuzCell = styled.p`
  font-size: 14px;
  font-weight: 400;
  overflow: hidden;
  white-space: nowrap;
  line-height: 20px;
  padding: 0px 0px 0px 16px;
  -o-text-overflow: ellipsis;
  -ms-text-overflow: ellipsis;
  text-overflow: ellipsis;
`;

const CustomHeader = styled.p`
  padding: 0px 0px 0px 16px;
  font-weight: bold;
  text-wrap-mode: nowrap;
  align-self: 'center';
  text-align: ${props => (props.left ? 'left' : 'center')};
`;

const DEFAULT_ROWS_PER_PAGE = 10;
const DEFAULT_ORDER_BY = '__name__';
const DEFAULT_ORDER_DIRECTION = 'asc';

function ManageKiosksPage() {
  const [data, setData] = useState([]);
  const [error, setError] = useState(false);
  const [snackbarVisible, setSnackBarVisible] = useState(false);
  const [snackbarMessage, setSnackBarMessage] = useState('');
  const [pageSize, setPageSize] = useState(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemCount, setItemCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchField, setSearchField] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);
  const [orderDirection, setOrderDirection] = useState(DEFAULT_ORDER_DIRECTION);
  const [cursors, setCursors] = useState(null);
  const { isDGAMLicensed, loadingLicense } = useDGAMLicenseCheck();
  const [selectedKiosk, setSelectedKiosk] = useState(null);
  const kioskFormModalRef = useRef();

  const context = useContext(AuthContext);
  // const { getKeyItem } = useContext(MessageResponseContext);
  const { t } = useTranslation();
  const { 
    canReadKiosk,
    // TODO: Please uncomment and enforce RBAC using the below when implementing the create, update, delete functionalities
    // canCreateKiosk, 
    canUpdateKiosk, 
    // canDeleteKiosk 
  } = usePermissions();

  useEffect(() => {
    if (!canReadKiosk) {
      return; // TODO: redirect/show unauthorized message
    }
    initManageKiosks();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!searchText) {
      setOrderBy(DEFAULT_ORDER_BY);
      setSearchField(null);
    } else {
      setOrderBy(searchField);
    }
  }, [searchText, searchField]);

  // Helper Functions
  async function initManageKiosks() {
    getData(pageSize, orderBy, orderDirection);
  }

  async function getData(currentPageSize, orderBy = DEFAULT_ORDER_BY, orderDirection = DEFAULT_ORDER_DIRECTION, shouldRefreshCount = false, currentSearchText = searchText, currentSearchField = searchField) {
    setIsLoading(true);
    try {
      setCurrentPage(0);
      
      // Get fresh count if needed
      if (shouldRefreshCount || itemCount === 0) {
        const count = await getTotalCount(context, { 
          searchText: currentSearchText, 
          searchField: currentSearchField, 
          orderDirection 
        });
        setItemCount(count);
      }

      const { kiosks, cursors: newCursors } = await fetchKiosks({
        context,
        limit: currentPageSize,
        searchField: currentSearchField,
        searchText: currentSearchText,
        orderBy,
        orderDirection,
        totalCount: itemCount
      });

      setData(kiosks);
      setCursors(newCursors);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching kiosks:', error);
      setIsLoading(false);
    }
  }

  function handleSearch(text, field) {
    setSearchText(text);
    setSearchField(field);
    handleFetchWithSearch(text, field);
  }

  function handleClearSearch() {
    setSearchText('');
    setSearchField(null);
    getData(pageSize, DEFAULT_ORDER_BY, orderDirection, true, '', null);
  }

  async function handleFetchWithSearch(text, field) {
    setIsLoading(true);
    setCurrentPage(0);
    setCursors(null);
  
    try {
      // Always get fresh count for new searches
      const count = await getTotalCount(context, { 
        searchText: text, 
        searchField: field, 
        orderDirection 
      });
      setItemCount(count);

      const { kiosks, cursors: newCursors } = await fetchKiosks({
        context,
        limit: pageSize,
        searchText: text,
        searchField: field,
        orderBy: field,
        orderDirection,
        totalCount: count
      });

      setData(kiosks);
      setCursors(newCursors);
      setIsLoading(false);
    } catch (error) {
      console.error('Error searching kiosks:', error);
      setIsLoading(false);
      setItemCount(0);
      setData([]);
    }
  }

  async function next() {
    if (!cursors?.last) return;

    setIsLoading(true);
    setCurrentPage(prev => prev + 1);

    try {
      const { kiosks, cursors: newCursors } = await fetchKiosks({
        context,
        limit: pageSize,
        searchField,
        searchText,
        orderBy: searchField || orderBy,
        orderDirection,
        startAfterDoc: cursors.last,
        totalCount: itemCount
      });

      if (kiosks.length > 0) {
        setData(kiosks);
        setCursors(newCursors);
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching next page:', error);
      setIsLoading(false);
    }
  }

  async function prev() {
    if (!cursors?.first) return;

    setIsLoading(true);
    setCurrentPage(prev => prev - 1);

    try {
      const { kiosks, cursors: newCursors } = await fetchKiosks({
        context,
        limit: pageSize,
        searchField,
        searchText,
        orderBy: searchField || orderBy,
        orderDirection,
        endBeforeDoc: cursors.first,
        totalCount: itemCount
      });

      if (kiosks.length > 0) {
        setData(kiosks);
        setCursors(newCursors);
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching previous page:', error);
      setIsLoading(false);
    }
  }

  async function handleFirstPage() {
    setIsLoading(true);
    setCurrentPage(0);
    try {
      const { kiosks, cursors: newCursors } = await fetchKiosks({
        context,
        limit: pageSize,
        searchField,
        searchText,
        orderBy: searchField || orderBy,
        orderDirection,
        totalCount: itemCount
      });

      setData(kiosks);
      setCursors(newCursors);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching first page:', error);
      setIsLoading(false);
    }
  }

  async function handleLastPage() {
    setIsLoading(true);
    try {
      const result = await fetchLastPage({
        context,
        limit: pageSize,
        searchField,
        searchText,
        orderBy: searchField || orderBy,
        orderDirection,
        totalCount: itemCount
      });

      if (result.kiosks.length > 0) {
        setData(result.kiosks);
        setCursors(result.cursors);

        const totalPages = Math.ceil(itemCount / pageSize);
        const lastPageNumber = Math.max(0, totalPages - 1);
        setCurrentPage(lastPageNumber);
      } else {
        setData([]);
        setCursors(null);
        setCurrentPage(0);
      }

      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching last page:', error);
      setData([]);
      setCursors(null);
      setCurrentPage(0);
      setIsLoading(false);
    }
  }

  async function handleSort(field, direction) {
    if (searchText && field !== searchField) return;

    setIsLoading(true);
    setCurrentPage(0);
    setCursors(null);

    try {
      setOrderBy(field);
      setOrderDirection(direction);

      const { kiosks, totalCount, cursors: newCursors } = await fetchKiosks({
        context,
        limit: pageSize,
        searchField,
        searchText,
        orderBy: field,
        orderDirection: direction,
        totalCount: itemCount
      });

      setData(kiosks);
      setItemCount(totalCount);
      setCursors(newCursors);
      setIsLoading(false);
    } catch (error) {
      console.error('Error sorting kiosks:', error);
      setIsLoading(false);
    }
  }

  async function handleRefresh() {
    setIsLoading(true);
    try {
      const { kiosks, cursors: newCursors } = await fetchKiosks({
        context,
        limit: pageSize,
        searchField,
        searchText,
        orderBy: searchField || orderBy,
        orderDirection,
        startAtDoc: cursors?.first,
        totalCount: itemCount
      });

      setData(kiosks);
      setCursors(newCursors);
      setIsLoading(false);
    } catch (error) {
      console.error('Error refreshing kiosks:', error);
      setIsLoading(false);
    }
  }

  // async function handleSort(field, direction) {
  //   if (searchText && field !== searchField) return;

  //   setIsLoading(true);
  //   setCurrentPage(0);
  //   setCursors(null);

  //   try {
  //     setOrderBy(field);
  //     setOrderDirection(direction);

  //     // TODO: Implement actual sorting functionality
  //     const sortedData = [...data].sort((a, b) => {
  //       const compareResult = a[field].localeCompare(b[field]);
  //       return direction === 'asc' ? compareResult : -compareResult;
  //     });

  //     setData(sortedData);
  //     setIsLoading(false);
  //   } catch (error) {
  //     console.error('Error sorting kiosks:', error);
  //     setIsLoading(false);
  //   }
  // }

  // Table Columns Configuration
  const columns = [
    {
      title: <CustomHeader left>{t('kioskName')}</CustomHeader>,
      field: '__name__',
      sorting: true,
      render: rowData => (
        <CuzCell>{rowData.__name__}</CuzCell>
      ),
    },
    {
      title: <CustomHeader left>{t('siteNameText')}</CustomHeader>,
      field: 'siteName',
      sorting: true,
      render: rowData => (
        <CuzCell>{rowData.siteName}</CuzCell>
      ),
    }
  ];

  const handleEdit = (kiosk) => {
    setSelectedKiosk(kiosk);
    kioskFormModalRef.current.toggleDialog();
  };

  function onModalClose() {
    setError(false);
    setSnackBarMessage(t('messages.success.success14'));
    setSnackBarVisible(true);
    getData(pageSize, orderBy, orderDirection, true);
  }

  return (
    <ContentWrapper
      style={{
        cursor: loadingLicense ? "wait" : "default",
        opacity: loadingLicense ? "0.4" : "1",
      }}
    >
      <CommonPageHeader>
        <CommonPageTitle>{t('manageKiosks')}</CommonPageTitle>
      </CommonPageHeader>

      {!isDGAMLicensed && !loadingLicense && (
        <NoAccessMessage>
          {t('licenseUpgradeText')}
        </NoAccessMessage>
      )}

      {isDGAMLicensed && !loadingLicense && (
        <>
          <Toolbar
            searchText={searchText}
            onSearch={(searchText, searchField) => {
              handleSearch(searchText, searchField);
            }}
            onClear={() => {
              handleClearSearch();
            }}
            multiSearchOpts={[
              { name: t('kioskName'), value: '__name__' },
              { name: t('siteNameText'), value: 'siteName' }
            ]}
            placeholderText={t('searchPlaceholderTextKiosk')}
          />

          <MainContent>
            <Table
              columns={columns}
              data={data}
              isLoading={isLoading}
              orderBy={orderBy}
              orderDirection={orderDirection}
              isSearchActive={!!searchText}
              searchField={searchField}
              onSort={(field, direction) => {
                handleSort(field, direction);
              }}
              pagination={{
                itemCount,
                currentPage,
                rowsPerPage: pageSize,
                rowsPerPageOptions: [10, 20, 50],
                onNextPageClicked: next,
                onPrevPageClicked: prev,
                onFirstPageClicked: handleFirstPage,
                onLastPageClicked: handleLastPage,
                onChangeRowsPerPage: (event) => {
                  const newSize = parseInt(event.target.value, 10);
                  setPageSize(newSize);
                  setCursors(null);
                  getData(newSize, orderBy, orderDirection, false, searchText, searchField);
                },
                hidePagination: itemCount <= DEFAULT_ROWS_PER_PAGE
              }}
              emptyMessage={t('messages.error.err57')}
              actions={[
                (rowData => ({
                  hidden: !canUpdateKiosk,
                  icon: () => <EditIcon />,
                  onClick: (event, rowData) => handleEdit(rowData),
                  tooltip: t('edit'),
                }))
              ].filter(Boolean)}
            />
          </MainContent>
        </>
      )}

      <BottomNavLinks />

      <KioskModal
          modalRef={kioskFormModalRef}
          initialData={selectedKiosk}
          onClose={() => {
              onModalClose();
          }}
        />

      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        open={snackbarVisible}
        autoHideDuration={5000}
        onClose={() => setSnackBarVisible(false)}
      >
        <SnackbarContent error={error}>{snackbarMessage}</SnackbarContent>
      </Snackbar>
    </ContentWrapper>
  );
}

export default withRBAC(ManageKiosksPage, RESOURCES.KIOSK_MANAGEMENT);